/**
 * WWW-4010 — Mini-cart gift message.
 *
 * Autosaves the textarea into `cart.note` (the field the checkout gift-message
 * option writes to) a short pause after the shopper stops typing.
 *
 * All listeners are delegated off `document` because the theme replaces the
 * whole `.Cart` node on every AJAX cart re-render (CartSection._replaceContent
 * in theme.js), which would drop any listener bound directly to the markup.
 */
(function () {
  'use strict';

  var SAVE_DELAY = 700;
  var CONTAINER = '[data-cart-gift-message]';

  var saveTimer = null;
  // Value typed but not yet confirmed saved. Kept so a cart re-render that
  // lands mid-save (e.g. the shopper changes a quantity right after typing)
  // can restore what they wrote instead of showing the stale server note.
  var pendingValue = null;
  var inFlight = null;

  function getContainer() {
    return document.querySelector(CONTAINER);
  }

  function getInput(container) {
    return container ? container.querySelector('[data-cart-gift-message-input]') : null;
  }

  function updateCounter(container) {
    var input = getInput(container);
    var counter = container.querySelector('[data-cart-gift-message-counter]');
    if (!input || !counter) return;

    var max = parseInt(container.getAttribute('data-max-length'), 10) || 0;

    // Before anything is typed the field advertises its limit; after that it
    // counts down instead.
    if (input.value.length > 0) {
      var remaining = Math.max(max - input.value.length, 0);
      counter.textContent = (counter.getAttribute('data-remaining-text') || '').replace('[count]', remaining);
    } else {
      counter.textContent = counter.getAttribute('data-max-text') || '';
    }
  }

  function showStatus(container, kind) {
    var status = container.querySelector('[data-cart-gift-message-status]');
    if (!status) return;

    if (!kind) {
      status.hidden = true;
      return;
    }

    var isError = kind === 'error';
    status.textContent = status.getAttribute(isError ? 'data-error-text' : 'data-saved-text') || '';
    status.classList.toggle('CartGiftMessage__Status--error', isError);
    status.hidden = false;
  }

  function setOpen(container, open) {
    var panel = container.querySelector('.CartGiftMessage__Panel');
    var toggle = container.querySelector('[data-cart-gift-message-toggle]');

    container.classList.toggle('is-open', open);
    if (panel) panel.hidden = !open;
    if (toggle) toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
  }

  function save(container, value) {
    var updateUrl = (window.routes && window.routes.cartUpdateUrl) || '/cart/update';

    inFlight = fetch(updateUrl + '.js', {
      body: JSON.stringify({ note: value }),
      credentials: 'same-origin',
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    }).then(function (response) {
      if (!response.ok) throw new Error('Cart update failed: ' + response.status);
      // Only clear the pending value if nothing newer was typed while in flight.
      if (pendingValue === value) pendingValue = null;
      showStatus(container, value.length > 0 ? 'saved' : null);
    }).catch(function () {
      showStatus(container, 'error');
    }).then(function () {
      inFlight = null;
    });

    return inFlight;
  }

  function scheduleSave(container) {
    var input = getInput(container);
    if (!input) return;

    pendingValue = input.value;
    window.clearTimeout(saveTimer);
    saveTimer = window.setTimeout(function () {
      save(container, pendingValue);
    }, SAVE_DELAY);
  }

  // Save immediately rather than waiting out the debounce — used on blur and
  // before the shopper leaves for checkout.
  function flush() {
    var container = getContainer();
    if (!container) return inFlight || Promise.resolve();

    var input = getInput(container);
    window.clearTimeout(saveTimer);

    if (input && pendingValue !== null && pendingValue === input.value) {
      return save(container, input.value);
    }

    return inFlight || Promise.resolve();
  }

  document.addEventListener('click', function (event) {
    var toggle = event.target.closest('[data-cart-gift-message-toggle]');
    if (!toggle) return;

    var container = toggle.closest(CONTAINER);
    if (!container) return;

    var willOpen = !container.classList.contains('is-open');
    setOpen(container, willOpen);

    if (willOpen) {
      var input = getInput(container);
      if (input) input.focus();
    }
  });

  document.addEventListener('input', function (event) {
    var input = event.target.closest('[data-cart-gift-message-input]');
    if (!input) return;

    var container = input.closest(CONTAINER);
    if (!container) return;

    showStatus(container, null);
    updateCounter(container);
    scheduleSave(container);
  });

  document.addEventListener('focusout', function (event) {
    if (event.target.closest('[data-cart-gift-message-input]')) flush();
  });

  // The drawer's checkout control is a plain link, so a note still sitting in
  // the debounce window would never reach the server. Hold the navigation for
  // as long as the save takes.
  document.addEventListener('click', function (event) {
    var checkout = event.target.closest('#sidebar-cart .Cart__Checkout');
    if (!checkout || pendingValue === null) return;

    event.preventDefault();
    flush().then(function () {
      window.location.href = checkout.getAttribute('href');
    });
  });

  // A cart re-render swaps in server markup built from the note as it was
  // before an in-flight save landed. Put the shopper's text back.
  function watchForRerender() {
    var drawer = document.getElementById('sidebar-cart');
    if (!drawer) return;

    new MutationObserver(function () {
      var container = getContainer();
      if (!container) return;

      if (pendingValue !== null) {
        var input = getInput(container);
        if (input && input.value !== pendingValue) {
          input.value = pendingValue;
          setOpen(container, true);
        }
      }

      updateCounter(container);
    }).observe(drawer, { childList: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchForRerender);
  } else {
    watchForRerender();
  }
})();
